import React, { useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

interface CodePopupModalProps {
  code: string;
  language: string;
  title: string;
  languageExtension: Extension;
  themeExtension?: Extension;
  isDarkMode: boolean;
  onClose: () => void;
}

const CodePopupModal: React.FC<CodePopupModalProps> = ({
  code,
  language,
  title,
  languageExtension,
  themeExtension,
  isDarkMode,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Handle click outside to close modal
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-8 z-50"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full h-full max-w-7xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {language.charAt(0).toUpperCase() + language.slice(1)} Solution
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Close (ESC)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Code Editor */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="codemirror-container readonly border border-gray-300 dark:border-gray-600 rounded">
            <CodeMirror
              value={code}
              height="calc(90vh - 200px)"
              extensions={[
                languageExtension,
                ...(themeExtension ? [themeExtension] : []),
                EditorView.theme({
                  '.cm-scroller': { overflow: 'auto' },
                  '.cm-content': { padding: '16px' }
                })
              ]}
              theme={isDarkMode ? 'dark' : 'light'}
              editable={false}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                dropCursor: false,
                allowMultipleSelections: false,
                indentOnInput: false,
                bracketMatching: true,
                closeBrackets: false,
                autocompletion: false,
                highlightSelectionMatches: false,
                searchKeymap: false
              }}
            />
          </div>
        </div>

        {/* Footer with hint */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Press <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">ESC</kbd> or click outside to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default CodePopupModal;
